import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Eye, Lock, ChevronUp, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Loading } from "@/components/common/Loading";
import { InfoHint } from "@/components/common/InfoHint";
import { ImageUploader, CoverUploader, VideoField } from "@/components/forms/MediaUploader";
import { RichTextEditor, FULL_TOOLBAR } from "@/components/forms/RichTextEditor";
import { MultiSelect } from "@/components/forms/MultiSelect";
import { TagInput } from "@/components/forms/TagInput";
import { LocationFields } from "@/components/forms/AddressFields";
import { AffiliationSearch } from "@/components/profile/AffiliationSearch";
import { useReferences } from "@/hooks/useReferences";
import { useAuth } from "@/context/AuthContext";

const refOpts = (list) => (list || []).map((x) => ({ value: x.value, label: x.label }));

// Background is rich text; count the visible characters (HTML stripped). Backend enforces the same cap.
const BG_MAX = 1300;
const PLAIN = (html) =>
  (html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .trim().length;

export default function MyProfile() {
  const { references } = useReferences();
  const { user, refreshUser } = useAuth();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locationFromAccount, setLocationFromAccount] = useState(false);
  // Orgs minted from the affiliation picker in THIS session — used only to offer the
  // owner a direct "Finish setup" link (opens in a new tab so unsaved edits survive).
  const [justCreated, setJustCreated] = useState({});

  const load = async () => {
    const { data } = await api.get("/profile/me");
    // Carry the account address over so members never type their city twice. Prefill only —
    // it stays editable and only sticks once they save.
    const addr = user?.address || {};
    if (!(data.location || {}).city && addr.city) {
      data.location = { city: addr.city || "", state: addr.state || "", zip: addr.zip || "", country: addr.country || "" };
      setLocationFromAccount(true);
    }
    setP(data);
  };
  useEffect(() => {
    load();
  }, []);

  if (!p) return <Loading />;
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const setLink = (k, v) => setP((s) => ({ ...s, links: { ...(s.links || {}), [k]: v } }));
  const isProPlus = ["pro", "premium", "business"].includes(user?.tier);

  const toggleOpenMessaging = (v) => { set("allow_open_messaging", v); save({ allow_open_messaging: v }); };

  const save = async (extra = {}) => {
    if (PLAIN(p.background) > BG_MAX) { toast.error("My Story must be 1,300 characters or fewer."); return; }
    setSaving(true);
    try {
      const payload = {
        display_name: p.display_name, handle: p.handle, slug: p.slug, pronouns: p.pronouns,
        headline: p.headline, background: p.background, headshot_url: p.headshot_url, cover_url: p.cover_url,
        location: p.location, industries: p.industries, classifications: p.classifications,
        skills: p.skills, intro_video: p.intro_video, links: p.links, allow_open_messaging: p.allow_open_messaging, ...extra,
      };
      const { data } = await api.put("/profile/me", payload);
      setP(data);
      await refreshUser();
      toast.success("Profile saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save profile.");
    } finally { setSaving(false); }
  };

  const togglePublish = async (v) => {
    await save({ published: v });
  };

  const addAffiliation = (resolved) => {
    setP((s) => ({ ...s, affiliations_resolved: resolved }));
  };
  const noteCreated = (org) => {
    if (org?.id) setJustCreated((s) => ({ ...s, [org.id]: org }));
  };
  const removeAffiliation = async (id) => {
    const { data } = await api.delete(`/profile/me/affiliations/${id}`);
    setP((s) => ({ ...s, affiliations_resolved: data.affiliations_resolved }));
  };
  const moveAffiliation = async (index, delta) => {
    const list = [...(p.affiliations_resolved || [])];
    const to = index + delta;
    if (to < 0 || to >= list.length) return;
    [list[index], list[to]] = [list[to], list[index]];
    setP((s) => ({ ...s, affiliations_resolved: list }));   // optimistic
    try {
      const { data } = await api.post("/profile/me/affiliations/reorder", {
        order: list.map((a) => a.id),
      });
      setP((s) => ({ ...s, affiliations_resolved: data.affiliations_resolved }));
    } catch {
      toast.error("Could not save that order.");
      setP((s) => ({ ...s, affiliations_resolved: p.affiliations_resolved }));
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">My Profile</h1>
          <p className="mt-1 text-muted-foreground">This is your personal profile on Startup Otter.</p>
        </div>
        <div className="flex items-center gap-3">
          {p.published && p.slug && (
            <Link to={`/p/${p.slug}`} target="_blank"><Button variant="secondary"><Eye className="mr-2 h-4 w-4" /> View public</Button></Link>
          )}
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2">
            <Switch checked={!!p.published} onCheckedChange={togglePublish} data-testid="profile-publish-toggle" />
            <span className="text-sm font-[540]">{p.published ? "Published" : "Hidden"}</span>
          </div>
          <Button onClick={() => save()} disabled={saving || PLAIN(p.background) > BG_MAX} data-testid="profile-save-button-top">{saving ? "Saving…" : "Save profile"}</Button>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <Card className="p-6">
          <h2 className="text-lg font-[640]">Cover Image</h2>
          <div className="mt-4"><CoverUploader value={p.cover_url} onChange={(u) => set("cover_url", u)} aspect={4} testId="profile-cover" /></div>
          <div className="mt-4"><ImageUploader value={p.headshot_url} onChange={(u) => set("headshot_url", u)} label="Headshot (square)" testId="profile-headshot" /></div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640]">Identity</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Display name *</Label><Input value={p.display_name || ""} onChange={(e) => set("display_name", e.target.value)} data-testid="profile-display-name" /></div>
            <div className="space-y-1.5"><Label className="inline-flex items-center">Handle * ({(p.handle || "").length}/50)<InfoHint testId="profile-handle-info" text="Your @handle is your unique identity across Startup Otter — it's global (no two members can share one) and works in @mentions. It's separate from your profile URL. Some brand and system names are reserved." /></Label>
              <div className="flex items-center rounded-lg border border-input bg-card px-3"><span className="text-muted-foreground">@</span><input maxLength={50} className="h-10 w-full bg-transparent px-1 text-sm outline-none" value={p.handle || ""} onChange={(e) => set("handle", e.target.value.slice(0, 50).replace(/[^a-zA-Z0-9-]/g, ""))} data-testid="profile-handle" /></div>
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="inline-flex items-center">Custom URL slug<InfoHint testId="profile-slug-info" text="The slug is the web address of your public profile (startupotter.com/p/your-slug). It only needs to be unique among personal profiles. Changing it makes your old link stop working — there are no redirects." /></Label>
              <div className="flex items-center rounded-lg border border-input bg-card px-3"><span className="text-sm text-muted-foreground">startupotter.com/p/</span><input className="h-10 w-full bg-transparent px-1 text-sm outline-none" value={p.slug || ""} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} data-testid="profile-slug" /></div>
            </div>
            <div className="space-y-1.5"><Label>Pronouns</Label>
              <Select value={p.pronouns || undefined} onValueChange={(v) => set("pronouns", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="profile-pronouns"><SelectValue placeholder="Select (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="profile-pronouns-none">— Not specified —</SelectItem>
                  {(references.pronouns || []).map((x) => <SelectItem key={x.value} value={x.label}>{x.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Headline ({(p.headline || "").length}/100)</Label><Input maxLength={100} value={p.headline || ""} onChange={(e) => set("headline", e.target.value)} data-testid="profile-headline" /></div>
          </div>
          <div className="mt-4 space-y-1.5"><Label>Location</Label><LocationFields value={p.location || {}} onChange={(l) => { setLocationFromAccount(false); set("location", l); }} testIdPrefix="profile-location" />
            {locationFromAccount && <p className="text-xs text-muted-foreground" data-testid="profile-location-from-account">Prefilled from your account address — edit it if your public location differs.</p>}
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640]">Intro Video</h2>
          <div className="mt-3"><VideoField value={p.intro_video} onChange={(v) => set("intro_video", v)} testId="profile-video" /></div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-[640]">My Story</h2>
            <span className={`text-xs tabular-nums ${PLAIN(p.background) > BG_MAX ? "font-[600] text-destructive" : "text-muted-foreground"}`} data-testid="profile-background-count">{PLAIN(p.background).toLocaleString()}/1,300</span>
          </div>
          <div className="mt-3"><RichTextEditor value={p.background} onChange={(v) => set("background", v)} toolbar={FULL_TOOLBAR} placeholder="Tell your story…" testId="profile-background" /></div>
          {PLAIN(p.background) > BG_MAX && (
            <p className="mt-2 text-xs text-destructive" data-testid="profile-background-error">My Story must be 1,300 characters or fewer. Please trim {(PLAIN(p.background) - BG_MAX).toLocaleString()} character{PLAIN(p.background) - BG_MAX === 1 ? "" : "s"}.</p>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640]">Affiliations</h2>
          <p className="mt-1 text-sm text-muted-foreground">Select companies, organizations, or capital groups you are affiliated with. This does not grant page access or management permissions.</p>
          <div className="mt-4">
            <AffiliationSearch onChange={addAffiliation} onCreated={noteCreated} />
          </div>
          {(p.affiliations_resolved || []).length > 0 && (
            <div className="mt-4 space-y-2">
              {p.affiliations_resolved.length > 1 && (
                <p className="text-xs text-muted-foreground" data-testid="affiliation-order-hint">Use the arrows to choose the order they appear on your public profile — the top one shows first.</p>
              )}
              {p.affiliations_resolved.map((a, i) => (
                <div key={a.id} className={`flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 ${a.visible === false ? "bg-muted/40 opacity-60" : "bg-secondary/40"}`} data-testid={`affiliation-item-${a.id}`}>
                  <span className="min-w-0 text-sm">
                    <strong>{a.display_name || "—"}</strong>
                    {a.role ? <span className="text-muted-foreground"> — {a.role}</span> : null}
                    {justCreated[a.target_id] && (
                      <span className="ml-2 inline-flex items-center gap-2">
                        <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-[600] uppercase tracking-wide text-muted-foreground">Draft</span>
                        <a
                          href={`/dashboard/${a.target_type === "capital" ? "capital-profiles" : "businesses"}/${a.target_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-[560] text-primary underline-offset-2 hover:underline"
                          data-testid={`affiliation-finish-setup-${a.target_id}`}
                        >
                          Finish setup
                        </a>
                      </span>
                    )}
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    {p.affiliations_resolved.length > 1 && (
                      <>
                        <button type="button" onClick={() => moveAffiliation(i, -1)} disabled={i === 0} aria-label="Move up" className="rounded p-1 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30" data-testid={`affiliation-move-up-${a.id}`}><ChevronUp className="h-4 w-4" /></button>
                        <button type="button" onClick={() => moveAffiliation(i, 1)} disabled={i === p.affiliations_resolved.length - 1} aria-label="Move down" className="rounded p-1 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30" data-testid={`affiliation-move-down-${a.id}`}><ChevronDown className="h-4 w-4" /></button>
                      </>
                    )}
                    <button onClick={() => removeAffiliation(a.id)} className="p-1 text-muted-foreground hover:text-destructive" data-testid={`affiliation-remove-${a.id}`}><Trash2 className="h-4 w-4" /></button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640]">Industries & Classifications</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Industries</Label><MultiSelect options={refOpts(references.industries)} value={p.industries || []} onChange={(v) => set("industries", v)} placeholder="Select industries" testId="profile-industries" /></div>
            <div className="space-y-1.5"><Label>Classifications</Label><MultiSelect options={refOpts(references.profile_classifications)} value={p.classifications || []} onChange={(v) => set("classifications", v)} placeholder="Select classifications" testId="profile-classifications" /></div>
          </div>
          <div className="mt-4 space-y-1.5"><Label>Skills & Interests</Label><TagInput options={refOpts(references.skills_interests)} value={p.skills || []} onChange={(v) => set("skills", v)} placeholder="Add a skill or interest" testId="profile-skills" maxLength={50} /></div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640]">Links</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {[["website", "Website"], ["linkedin", "LinkedIn"], ["x", "X (Twitter)"], ["facebook", "Facebook"], ["instagram", "Instagram"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5"><Label>{l}</Label><Input value={(p.links || {})[k] || ""} onChange={(e) => setLink(k, e.target.value)} placeholder="https://…" data-testid={`profile-link-${k}`} /></div>
            ))}
          </div>
        </Card>

        <Card className="p-6" data-testid="profile-messaging-privacy">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-[640]">
                Messaging privacy
                {!isProPlus && <Lock className="h-4 w-4 text-muted-foreground" data-testid="profile-open-messaging-lock" />}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Let members you&apos;re not connected with start a conversation with you. Your connections can always message you.
                {!isProPlus && <span className="ml-1 font-[560] text-foreground">Available on PRO and above.</span>}
              </p>
            </div>
            {isProPlus ? (
              <Switch
                checked={!!p.allow_open_messaging}
                onCheckedChange={toggleOpenMessaging}
                disabled={saving}
                data-testid="profile-open-messaging-toggle"
              />
            ) : (
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border bg-secondary/40 px-2.5 py-1.5 text-muted-foreground transition-colors hover:text-foreground" data-testid="profile-open-messaging-locked">
                    <span className="pointer-events-none inline-flex h-5 w-9 items-center rounded-full border-2 border-transparent bg-input opacity-60" aria-hidden="true">
                      <span className="block h-4 w-4 translate-x-0 rounded-full bg-background shadow-lg" />
                    </span>
                    <Lock className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-72" data-testid="profile-open-messaging-popover">
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2 font-[640] text-foreground"><Lock className="h-4 w-4 text-primary" /> A PRO feature</div>
                    <p className="text-sm text-muted-foreground">Open your inbox to members you&apos;re not connected with. Upgrade to PRO or above to switch this on.</p>
                    <Button asChild size="sm" className="w-full" data-testid="profile-open-messaging-upgrade">
                      <Link to="/pricing">Upgrade your plan</Link>
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </Card>

        <Separator />
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{p.published ? "Your profile is live." : "Hidden profiles can browse but not interact."}</p>
          <Button size="lg" onClick={() => save()} disabled={saving || PLAIN(p.background) > BG_MAX} data-testid="profile-save-button">{saving ? "Saving…" : "Save profile"}</Button>
        </div>
      </div>
    </div>
  );
}
